import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { getRecaptchaToken } from "@/hooks/useRecaptcha";
import { projectDetailsSchema } from "@/lib/projectDetails";

interface CTAContactFormProps {
  serviceName?: string;
}

export function CTAContactForm({ serviceName }: CTAContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [showPrivacyError, setShowPrivacyError] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // bot detected
    const details = projectDetailsSchema.safeParse(formData.message);
    if (!details.success) {
      toast.error(details.error.issues[0]?.message || "Please tell us about your project.");
      return;
    }
    if (!privacyConsent) {
      setShowPrivacyError(true);
      return;
    }
    setIsSubmitting(true);

    try {
      const recaptchaToken = await getRecaptchaToken("submit_lead");
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("submit-lead", {
        body: { ...formData, source: serviceName || "Website", privacyConsent, smsConsent, recaptchaToken },
      });

      if (error) throw error;

      toast.success("Thank you! We'll be in touch soon.");

      // Conversion tracking — fire only on confirmed successful submission
      const isLandingPage = window.location.pathname.startsWith('/lp/paver-patios');
      const formName = isLandingPage ? 'landing_page_paver_patios' : 'site_contact_form';
      const conversionSendTo = isLandingPage
        ? 'AW-10778546685/dJAXCPnY164cEP2jzpMo'
        : 'AW-10778546685/ndr-COa_7KAcEP2jzpMo';

      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'form_submit', {
          form_name: formName,
          page_location: window.location.href,
          page_path: window.location.pathname,
        });
        (window as any).gtag('event', 'conversion', {
          send_to: conversionSendTo,
          value: 1.0,
          currency: 'USD',
        });
      }

      setFormData({ name: "", email: "", phone: "", address: "", city: "", state: "", zipCode: "", message: "" });
      setPrivacyConsent(false);
      setSmsConsent(false);
      setShowPrivacyError(false);
    } catch (err) {
      console.error("Form submission error:", err);
      toast.error("Something went wrong. Please call or text us at (973) 725-2395.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot field - hidden from humans */}
      <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
        <label htmlFor="cta-website">Website</label>
        <input
          id="cta-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="cta-name" className="sr-only">Name</Label>
        <Input
          id="cta-name"
          name="name"
          type="text"
          placeholder="Name*"
          required
          value={formData.name}
          onChange={handleChange}
          className="bg-white text-black border-border h-12"
        />
      </div>

      <div>
        <Label htmlFor="cta-email" className="sr-only">Email</Label>
        <Input
          id="cta-email"
          name="email"
          type="email"
          placeholder="Email*"
          required
          value={formData.email}
          onChange={handleChange}
          className="bg-white text-black border-border h-12"
        />
      </div>

      <div>
        <Label htmlFor="cta-phone" className="sr-only">Phone Number</Label>
        <Input
          id="cta-phone"
          name="phone"
          type="tel"
          placeholder="Phone Number*"
          required
          value={formData.phone}
          onChange={handleChange}
          className="bg-white text-black border-border h-12"
        />
      </div>

      <div>
        <Label htmlFor="cta-address" className="sr-only">Address</Label>
        <Input
          id="cta-address"
          name="address"
          type="text"
          placeholder="Address*"
          required
          value={formData.address}
          onChange={handleChange}
          className="bg-white text-black border-border h-12"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="cta-city" className="sr-only">City</Label>
          <Input
            id="cta-city"
            name="city"
            type="text"
            placeholder="City*"
            required
            value={formData.city}
            onChange={handleChange}
            className="bg-white text-black border-border h-12"
          />
        </div>
        <div>
          <Label htmlFor="cta-state" className="sr-only">State</Label>
          <Input
            id="cta-state"
            name="state"
            type="text"
            placeholder="State*"
            required
            value={formData.state}
            onChange={handleChange}
            className="bg-white text-black border-border h-12"
          />
        </div>
        <div>
          <Label htmlFor="cta-zipCode" className="sr-only">Zip Code</Label>
          <Input
            id="cta-zipCode"
            name="zipCode"
            type="text"
            placeholder="Zip Code*"
            required
            value={formData.zipCode}
            onChange={handleChange}
            className="bg-white text-black border-border h-12"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="cta-message" className="sr-only">Message</Label>
        <Textarea
          id="cta-message"
          name="message"
          placeholder={serviceName && serviceName !== "Hero Form" ? `Tell us about your ${serviceName.toLowerCase()} project*` : "Tell us about your project*"}
          required
          maxLength={500}
          rows={4}
          value={formData.message}
          onChange={handleChange}
          className="bg-white text-black border-border resize-none"
        />
      </div>

      {/* Privacy Policy Consent */}
      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <Checkbox
            id="cta-privacy-consent"
            checked={privacyConsent}
            onCheckedChange={(checked) => {
              setPrivacyConsent(checked === true);
              if (checked) setShowPrivacyError(false);
            }}
            className="mt-1"
          />
          <Label htmlFor="cta-privacy-consent" className="text-sm text-muted-foreground font-normal leading-snug cursor-pointer">
            I agree to the{" "}
            <Link to="/privacy-policy" className="text-secondary underline hover:text-secondary/80">
              Privacy Policy
            </Link>{" "}
            and consent to being contacted by Masonry Pro Construction regarding my inquiry.
          </Label>
        </div>
        {showPrivacyError && (
          <p className="text-sm text-destructive ml-6">
            You must agree to our Privacy Policy to submit this request. Without this we are unable to contact you regarding your inquiry.
          </p>
        )}
      </div>

      {/* SMS Consent */}
      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <Checkbox
            id="cta-sms-consent"
            checked={smsConsent}
            onCheckedChange={(checked) => setSmsConsent(checked === true)}
            className="mt-1"
          />
          <Label htmlFor="cta-sms-consent" className="text-sm text-muted-foreground font-normal leading-snug cursor-pointer">
            I consent to receive text messages from Masonry Pro Construction regarding my estimate and project updates. Message and data rates may apply. Reply STOP to opt out.
          </Label>
        </div>
        {!smsConsent && (
          <p className="text-xs text-muted-foreground/70 ml-6">
            Note: Without SMS consent we will only be able to reach you by phone call or email.
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">* Required. Good details now mean an honest, accurate estimate later.</p>

      <Button
        type="submit"
        variant="gold"
        size="lg"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "SEND"}
      </Button>

      <p className="text-xs text-muted-foreground/60 text-center leading-snug">
        This site is protected by reCAPTCHA and the Google{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">Privacy Policy</a> and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">Terms of Service</a> apply.
      </p>
    </form>
  );
}
